import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { FileText, User, Clock, CheckCircle, AlertCircle, Eye, Download, Mail, X, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ReviewSubmitPage from '../admin/ReviewSubmitPage';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';
import { pdf } from '@react-pdf/renderer';
import PatientRecordPDF from '../pdf/PatientRecordPDF';

const PatientSubmissionsList = ({ doctorSubmissions, handleSubmissionClick, selectedSubmission, onRefresh }) => {
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null); // PDF URL for modal display
  
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'list'
  const [searchTerm, setSearchTerm] = useState(''); // Search by patient or doctor name
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset to first page when view mode or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, searchTerm]);
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-yellow-400" />;
      default:
        return <AlertCircle className="h-5 w-5 text-blue-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'in-progress':
        return 'text-yellow-400';
      default:
        return 'text-blue-400';
    }
  };

  const getPriorityColor = (painLevel) => {
    const level = parseInt(painLevel);
    if (level >= 7) return 'text-red-400';
    if (level >= 4) return 'text-yellow-400';
    return 'text-green-400';
  };

  const handleView = async (submission) => {
    try {
      // First, check if PDF URL exists in submission data (same as Print button)
      let pdfUrl = submission.pdf_url;
      
      // If not in submission object, fetch full submission data
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }
      
      // If PDF URL exists, show PDF in modal (instead of opening new window)
      if (pdfUrl) {
        // Convert relative URL to absolute if needed
        const absoluteUrl = pdfUrl.startsWith('http') 
          ? pdfUrl 
          : `${window.location.origin}${pdfUrl.startsWith('/') ? '' : '/'}${pdfUrl}`;
        
        // Store PDF URL and show modal
        setViewingPdfUrl(absoluteUrl);
        setViewingSubmission(submission);
        return; // Exit early - PDF will be shown in modal
      }
      
      // Fallback: If no PDF URL exists, open modal with ReviewSubmitPage (generate/view data)
      setViewingPdfUrl(null); // Clear PDF URL
      setViewingSubmission(submission);
      setLoadingData(true);
      
      try {
        // Fetch full submission data including medical history, dental examination, and procedures
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          // Transform procedures data to match expected format
          // API returns procedures as array, but ReviewSubmitPage expects { rows: [...] } or { procedures: [...] }
          const transformedData = {
            ...data,
            procedures: Array.isArray(data.procedures) 
              ? { rows: data.procedures }
              : (data.procedures || null),
            // Include doctor name for display
            doctor_name: data.doctor_name || submission.doctors?.name || null
          };
          setSubmissionData(transformedData);
        } else {
          toast({
            title: 'Error',
            description: 'Failed to load submission details',
            variant: 'destructive'
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to load submission details',
          variant: 'destructive'
        });
      } finally {
        setLoadingData(false);
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF',
        variant: 'destructive'
      });
    }
  };

  const handlePrint = async (submission) => {
    try {
      // Check if PDF URL exists in submission data
      let pdfUrl = submission.pdf_url;
      
      console.log('=== PRINT BUTTON CLICKED ===');
      console.log('Submission ID:', submission.id);
      console.log('PDF URL from submission object:', pdfUrl);
      
      // If not in submission object, fetch full submission data
      if (!pdfUrl) {
        console.log('PDF URL not found in submission object, fetching full submission data...');
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
          console.log('PDF URL from API response:', pdfUrl);
        } else {
          console.log('Failed to fetch submission data:', data);
        }
      }
      
      if (pdfUrl) {
        // Open PDF URL in new window/tab for printing
        // Convert relative URL to absolute if needed
        const absoluteUrl = pdfUrl.startsWith('http') 
          ? pdfUrl 
          : `${window.location.origin}${pdfUrl.startsWith('/') ? '' : '/'}${pdfUrl}`;
        
        console.log('Original PDF URL:', pdfUrl);
        console.log('Absolute PDF URL:', absoluteUrl);
        console.log('Window origin:', window.location.origin);
        console.log('Opening PDF in new window...');
        
        const printWindow = window.open(absoluteUrl, '_blank');
        
        if (printWindow) {
          console.log('PDF window opened successfully');
          // Wait for PDF to load, then trigger print dialog
          printWindow.onload = () => {
            console.log('PDF loaded in new window, triggering print dialog...');
            setTimeout(() => {
              printWindow.print();
            }, 500);
          };
        } else {
          console.error('Failed to open PDF window (pop-up blocked?)');
          toast({
            title: 'Error',
            description: 'Please allow pop-ups to print the PDF',
            variant: 'destructive'
          });
        }
      } else {
        console.warn('No PDF URL found, falling back to view modal print');
        // If no PDF URL exists, fall back to opening view modal and printing
        handleView(submission);
        setTimeout(() => {
          window.print();
        }, 500);
      }
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF for printing',
        variant: 'destructive'
      });
      // Fallback to view modal print
      handleView(submission);
      setTimeout(() => {
        window.print();
      }, 500);
    }
  };

  const handleEmail = async (submission) => {
    try {
      // Show loading toast
      toast({
        title: 'Preparing email...',
        description: 'Please wait while we prepare your email.',
      });

      // Get PDF URL the same way as View and Print buttons do
      // First, check if PDF URL exists in submission data
      let pdfUrl = submission.pdf_url;
      
      // If not in submission object, fetch full submission data from API
      // This ensures we get the correct PDF URL for this specific appointment
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }

      // If PDF URL still doesn't exist, show error (don't generate new PDF - use the one from database)
      if (!pdfUrl) {
        throw new Error('PDF not found for this appointment. Please ensure the patient record was submitted with a PDF.');
      }

      // Send email with PDF link
      // Backend will use the PDF URL from the submission record in database (ensures correct PDF per appointment)
      const emailData = await apiRequest('email_patient_record.php', {
        method: 'POST',
        body: JSON.stringify({ 
          submission_id: submission.id
          // Note: We don't send pdf_url - backend will use the one from submission record
        })
      });
      
      if (emailData.success) {
        toast({
          title: 'Success',
          description: 'Patient record sent via email with PDF download link'
        });
      } else {
        // Check if it's a configuration error (Brevo disabled)
        const isConfigError = emailData.message?.includes('disabled') || 
                              emailData.message?.includes('not configured') ||
                              emailData.message?.includes('API key');
        
        toast({
          title: 'Email Service Not Configured',
          description: isConfigError 
            ? 'Email service is not configured. Please contact your administrator to set up email service.'
            : (emailData.message || emailData.error || 'Failed to send email'),
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error.message || 'Failed to send email';
      const isConfigError = errorMessage.includes('disabled') || 
                           errorMessage.includes('not configured') ||
                           errorMessage.includes('Brevo email service is disabled');
      
      toast({
        title: isConfigError ? 'Email Service Not Configured' : 'Error',
        description: isConfigError
          ? 'Email service is not configured. Please contact your administrator to set up email service.'
          : errorMessage,
        variant: 'destructive'
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <CardTitle className="text-white flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-400" />
              Completed Patient's Appointment History
            </CardTitle>
            {/* View Toggle - Right side with spacing */}
            <div className="flex items-center gap-2 ml-4">
              <Label htmlFor="view-mode-patient-submissions" className="text-white text-sm cursor-pointer whitespace-nowrap">
                List View
              </Label>
              <input
                type="checkbox"
                id="view-mode-patient-submissions"
                checked={viewMode === 'list'}
                onChange={(e) => setViewMode(e.target.checked ? 'list' : 'detailed')}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
              />
            </div>
          </div>
          <CardDescription className="text-gray-300">
            Click on a submission to view, print, Email patient dental history and Invoice
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search Filter */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by patient name or doctor name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white pl-8 h-9"
              />
            </div>
            {searchTerm && (
              <p className="text-xs text-gray-400 mt-1">
                Filtering by: {searchTerm}
              </p>
            )}
          </div>

          {/* Filter submissions based on search term */}
          {(() => {
            const filteredSubmissions = doctorSubmissions.filter(submission => {
              // Filter by search term
              if (!searchTerm.trim()) return true;
              const searchLower = searchTerm.toLowerCase();
              const patientName = (submission.profiles?.name || '').toLowerCase();
              const doctorName = (submission.doctors?.name || '').toLowerCase();
              return patientName.includes(searchLower) || doctorName.includes(searchLower);
            });

            return filteredSubmissions.length > 0 ? (
              viewMode === 'list' ? (
                /* List View with Pagination */
                <>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(() => {
                      const sortedSubmissions = filteredSubmissions.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
                      const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
                      const startIndex = (currentPage - 1) * itemsPerPage;
                      const endIndex = startIndex + itemsPerPage;
                      const paginatedSubmissions = sortedSubmissions.slice(startIndex, endIndex);

                      return paginatedSubmissions.map((submission) => (
                    <div
                      key={submission.id}
                      className="p-3 bg-gray-800 rounded-lg flex items-center justify-between gap-2 sm:gap-3 flex-wrap"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                          submission.status === 'completed' ? 'bg-green-600 text-green-100' :
                          submission.status === 'in-progress' ? 'bg-yellow-600 text-yellow-100' :
                          'bg-blue-600 text-blue-100'
                        }`}>
                          {submission.status || 'pending'}
                        </span>
                        <FileText className="h-4 w-4 text-blue-400 flex-shrink-0" />
                        <p className="text-white font-medium truncate text-sm sm:text-base">
                          {submission.profiles?.name || 'Unknown Patient'}
                        </p>
                        {submission.profiles?.patient_unique_id && (
                          <>
                            <span className="text-gray-400 text-sm">({submission.profiles.patient_unique_id})</span>
                          </>
                        )}
                        {(submission.appointment_reference_id || submission.appointment_id) && (
                          <span className="text-blue-400 text-xs font-mono bg-blue-500/20 px-2 py-0.5 rounded flex-shrink-0">
                            {submission.appointment_reference_id || `ID: ${submission.appointment_id}`}
                          </span>
                        )}
                        <span className="text-gray-500 hidden sm:inline">•</span>
                        <p className="text-gray-400 text-xs sm:text-sm truncate">
                          {new Date(submission.submitted_at).toLocaleDateString()}
                        </p>
                        {submission.doctors?.name && (
                          <>
                            <span className="text-gray-500 hidden sm:inline">•</span>
                            <p className="text-gray-400 text-xs sm:text-sm truncate">
                              Dr. {submission.doctors.name}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex gap-1 sm:gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(submission)}
                          className="bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                          title="View"
                        >
                          <Eye className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrint(submission)}
                          className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                          title="Print"
                        >
                          <Download className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">Print</span>
                        </Button>
                        {(submission.profiles?.email || submission.patient_email) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEmail(submission)}
                            className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                            title="Email"
                          >
                            <Mail className="h-3 w-3 sm:mr-1" />
                            <span className="hidden sm:inline">Email</span>
                          </Button>
                        )}
                      </div>
                    </div>
                      ));
                    })()}
                  </div>
                  
                  {/* Pagination Controls for List View */}
                  {(() => {
                    const sortedSubmissions = filteredSubmissions.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
                    const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
                    if (sortedSubmissions.length <= itemsPerPage) return null;
                    
                    return (
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700 mt-4">
                        <div className="text-sm text-gray-400">
                          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedSubmissions.length)} of {sortedSubmissions.length} submissions
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </Button>
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                              if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                              ) {
                                return (
                                  <Button
                                    key={page}
                                    variant={currentPage === page ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setCurrentPage(page)}
                                    className={
                                      currentPage === page
                                        ? "bg-blue-600 text-white hover:bg-blue-700"
                                        : "border-gray-600 text-gray-300 hover:bg-gray-700"
                                    }
                                  >
                                    {page}
                                  </Button>
                                );
                              } else if (
                                page === currentPage - 2 ||
                                page === currentPage + 2
                              ) {
                                return (
                                  <span key={page} className="text-gray-500 px-2">
                                    ...
                                  </span>
                                );
                              }
                              return null;
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage >= totalPages}
                            className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                /* Detailed View */
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredSubmissions
                    .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
                    .map((submission) => (
                    <div
                      key={submission.id}
                      className={`p-4 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors ${
                        selectedSubmission?.id === submission.id ? 'ring-2 ring-blue-400' : ''
                      }`}
                      onClick={() => handleSubmissionClick(submission)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2 flex-wrap">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-white font-medium">{submission.profiles?.name || 'Unknown Patient'}</span>
                          {submission.profiles?.patient_unique_id && (
                            <span className="text-gray-400 text-sm">({submission.profiles.patient_unique_id})</span>
                          )}
                          {(submission.appointment_reference_id || submission.appointment_id) && (
                            <span className="text-blue-400 text-xs font-mono bg-blue-500/20 px-2 py-0.5 rounded">
                              {submission.appointment_reference_id || `ID: ${submission.appointment_id}`}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(submission.status)}
                          <span className={`text-sm ${getStatusColor(submission.status)}`}>
                            {submission.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Submitted</p>
                          <p className="text-white">
                            {new Date(submission.submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-400">Status</p>
                          <p className={`font-medium ${getStatusColor(submission.status)}`}>
                            {submission.status || 'pending'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-2">
                        <p className="text-gray-400 text-sm">Chief Complaint</p>
                        <p className="text-white text-sm truncate">
                          {submission.chief_complaint || 'No complaint specified'}
                        </p>
                      </div>
                      
                      {submission.doctors?.name && (
                        <div className="mt-2">
                          <p className="text-gray-400 text-sm">Submitted By</p>
                          <p className="text-white text-sm">
                            Dr. {submission.doctors.name}
                          </p>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(submission)}
                          className="flex-1 bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrint(submission)}
                          className="flex-1 bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Print
                        </Button>
                        {(submission.profiles?.email || submission.patient_email) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEmail(submission)}
                            className="flex-1 bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs"
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            Email
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400">
                  {searchTerm 
                    ? 'No submissions found matching your search' 
                    : 'No patient submissions yet'}
                </p>
                {searchTerm && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSearchTerm('')}
                    className="mt-4 border-gray-600 text-gray-300 hover:bg-gray-700"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear Search
                  </Button>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={viewingSubmission !== null} onOpenChange={(open) => {
        if (!open) {
          setViewingSubmission(null);
          setViewingPdfUrl(null); // Clear PDF URL when modal closes
        }
      }}>
        <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-7xl w-[98vw] max-h-[95vh] h-[95vh] p-0 flex flex-col [&>button]:hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white text-xl">
                Patient Record and Invoice
                {viewingSubmission?.appointment_reference_id && (
                  <span className="text-gray-400 text-sm ml-2">
                    - {viewingSubmission.appointment_reference_id}
                  </span>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {viewingPdfUrl && !isMobile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (viewingPdfUrl) {
                        // Extract filename from URL to maintain consistency with uploaded filename
                        let filename = 'Patient-Record.pdf'; // Default fallback
                        
                        try {
                          // Extract filename from URL path
                          const urlPath = viewingPdfUrl.split('?')[0]; // Remove query parameters
                          const pathParts = urlPath.split('/');
                          const urlFilename = pathParts[pathParts.length - 1];
                          
                          // If filename exists in URL, use it; otherwise use default
                          if (urlFilename && urlFilename.endsWith('.pdf')) {
                            filename = urlFilename;
                          } else {
                            // Fallback: construct from submission data
                            filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                          }
                        } catch (error) {
                          console.error('Error extracting filename from URL:', error);
                          // Fallback: construct from submission data
                          filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                        }
                        
                        const link = document.createElement('a');
                        link.href = viewingPdfUrl;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        toast({
                          title: 'Download started',
                          description: 'Your PDF is being downloaded',
                        });
                      }
                    }}
                    className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setViewingSubmission(null);
                    setViewingPdfUrl(null);
                  }}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* PDF Viewer - If PDF URL exists, show PDF */}
          {viewingPdfUrl ? (
            isMobile ? (
              // Mobile: Open PDF in new window (better compatibility with Safari/Chrome mobile)
              <div 
                className="flex-1 flex flex-col items-center justify-center p-6"
                style={{ 
                  minHeight: 'calc(95vh - 120px)',
                  height: 'calc(95vh - 120px)'
                }}
              >
                <div className="text-center space-y-4">
                  <FileText className="h-16 w-16 text-blue-400 mx-auto" />
                  <h3 className="text-white text-lg font-semibold">View PDF on Mobile</h3>
                  <p className="text-gray-300 text-sm max-w-md">
                    For the best viewing experience on mobile devices, the PDF will open in a new window where you can view all pages and use native PDF controls.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <Button
                      onClick={() => {
                        window.open(viewingPdfUrl, '_blank');
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
                      size="lg"
                    >
                      <Eye className="h-5 w-5 mr-2" />
                      Open PDF in New Window
                    </Button>
                    <Button
                      onClick={() => {
                        if (viewingPdfUrl) {
                          // Extract filename from URL to maintain consistency with uploaded filename
                          let filename = 'Patient-Record.pdf'; // Default fallback
                          
                          try {
                            // Extract filename from URL path
                            const urlPath = viewingPdfUrl.split('?')[0]; // Remove query parameters
                            const pathParts = urlPath.split('/');
                            const urlFilename = pathParts[pathParts.length - 1];
                            
                            // If filename exists in URL, use it; otherwise use default
                            if (urlFilename && urlFilename.endsWith('.pdf')) {
                              filename = urlFilename;
                            } else {
                              // Fallback: construct from submission data
                              filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                            }
                          } catch (error) {
                            console.error('Error extracting filename from URL:', error);
                            // Fallback: construct from submission data
                            filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                          }
                          
                          const link = document.createElement('a');
                          link.href = viewingPdfUrl;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          toast({
                            title: 'Download started',
                            description: 'Your PDF is being downloaded',
                          });
                        }
                      }}
                      variant="outline"
                      className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white px-6 py-3"
                      size="lg"
                    >
                      <Download className="h-5 w-5 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // Desktop: Use embed tag for better performance and native PDF rendering
              <div 
                className="flex-1 overflow-y-auto overflow-x-hidden" 
                style={{ 
                  minHeight: 'calc(95vh - 120px)',
                  maxHeight: 'calc(95vh - 120px)',
                  height: 'calc(95vh - 120px)',
                  WebkitOverflowScrolling: 'touch',
                  position: 'relative'
                }}
              >
                <embed
                  src={`${viewingPdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&zoom=page-width`}
                  type="application/pdf"
                  className="w-full"
                  style={{ 
                    minHeight: 'calc(95vh - 120px)',
                    width: '100%',
                    display: 'block',
                    border: 'none'
                  }}
                />
              </div>
            )
          ) : (
            <>
              {!loadingData && submissionData && (
                <div className="px-6 pt-4 pb-2">
                  <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!submissionData) return;
                  
                  try {
                    // Show loading toast
                    toast({
                      title: 'Generating PDF...',
                      description: 'Please wait while we create your PDF file.',
                    });

                    // Generate filename - Standard format: Patient-Record-{PatientName}-{AppointmentReferenceID}-{Date}-{Timestamp}-{Random}.pdf
                    // Add timestamp and random suffix to prevent overwrites
                    const patientName = submissionData?.patient_name || viewingSubmission?.profiles?.name || 'Patient';
                    const appointmentReferenceId = submissionData?.appointment_reference_id || submissionData?.appointment_id || viewingSubmission?.appointment_reference_id || viewingSubmission?.appointment_id || 'Record';
                    const date = new Date().toISOString().split('T')[0];
                    const sanitizedPatientName = patientName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
                    // Add high-precision timestamp (milliseconds + random) to ensure absolute uniqueness and prevent overwrites
                    const timestamp = Date.now();
                    const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
                    const uniqueId = `${timestamp}-${randomSuffix}`;
                    const filename = `Patient-Record-${sanitizedPatientName}-${appointmentReferenceId}-${date}-${uniqueId}.pdf`;

                    // Transform procedures data for PDF (ensure it's in the correct format)
                    let proceduresForPDF = null;
                    if (submissionData.procedures) {
                      if (Array.isArray(submissionData.procedures)) {
                        proceduresForPDF = { rows: submissionData.procedures };
                      } else if (submissionData.procedures.rows || submissionData.procedures.procedures) {
                        proceduresForPDF = submissionData.procedures;
                      }
                    }

                    // Get doctor name from submission data
                    const doctorName = submissionData.doctor_name || viewingSubmission?.doctors?.name || 'N/A';
                    
                    // Create PDF document
                    const doc = (
                      <PatientRecordPDF
                        patientInfo={{
                          name: submissionData.patient_name || viewingSubmission?.profiles?.name || 'N/A',
                          phone: submissionData.patient_phone || viewingSubmission?.profiles?.phone || 'N/A',
                          address: submissionData.patient_address || viewingSubmission?.profiles?.address || 'N/A',
                        }}
                        appointmentDetails={{
                          referenceId: submissionData.appointment_reference_id || submissionData.appointment_id || 'N/A',
                          id: submissionData.appointment_id || viewingSubmission?.appointment_id || 'N/A',
                        }}
                        medicalHistory={submissionData.medical_history || null}
                        dentalExamination={submissionData.dental_examination || null}
                        procedures={proceduresForPDF}
                        profile={{
                          name: doctorName,
                          full_name: doctorName
                        }}
                      />
                    );

                    // Generate PDF blob
                    const blob = await pdf(doc).toBlob();
                    
                    // Create download link
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);

                    toast({
                      title: 'PDF Saved',
                      description: `Patient record saved as ${filename}`,
                    });
                  } catch (error) {
                    console.error('Error generating PDF:', error);
                    toast({
                      title: 'Error',
                      description: error.message || 'Failed to generate PDF. Please try again.',
                      variant: 'destructive',
                    });
                  }
                }}
                      className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-0 hover:from-blue-700 hover:to-cyan-700 shadow-lg hover:shadow-xl transition-all"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  )}
                  <div className="flex-1 overflow-y-auto px-6 py-4" id="pdf-content-wrapper">
                    {loadingData ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                      </div>
                    ) : submissionData ? (
                      <ReviewSubmitPage
                        patientId={submissionData.patient_id}
                        patientName={submissionData.patient_name}
                        appointmentId={submissionData.appointment_id || viewingSubmission?.appointment_id || null}
                        medicalHistory={submissionData.medical_history}
                        dentalExamination={submissionData.dental_examination}
                        procedures={
                          // Transform procedures data to match expected format
                          Array.isArray(submissionData.procedures) 
                            ? { rows: submissionData.procedures }
                            : submissionData.procedures || null
                        }
                        hideDialog={true}
                        onBack={null}
                        onSubmit={null}
                        // Pass doctor name for "Submitted By" display
                        doctorName={submissionData.doctor_name || viewingSubmission?.doctors?.name || null}
                      />
                    ) : null}
                  </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default PatientSubmissionsList;